import React, {useEffect, useState} from "react"

import FormattedMessage from "@irati/utils/lang"

import useAuth from "@irati/auth/helpers/auth/useAuth"
import Flex from "@irati/ui/src/Flex"
import {Card} from "@irati/ui/src/Layout"
import styled, {useTheme, css} from "styled-components"
import {H1, H2} from "@irati/ui/src/Typography"
import Button, {IconTypes} from "@irati/ui/src/Button"
import {createUseLazyQuery} from "@irati/utils/services/apollo"
import {trackingOnlineGP} from "@irati-tracking/online-gp"
import {Assertion, QueryGenerateAssertionArgs} from "@irati/types"
import {gql} from "@irati/utils/gql"
import Loading from "@irati/ui/src/Loading"
import Markdown from "@irati/ui/src/Markdown"
import Icon from "@irati/ui/src/Icon"
import Spacing from "@irati/ui/src/Spacing"
import healthHero from "../images/health-hero.svg"

const useGetSamlQuery = createUseLazyQuery<Assertion, QueryGenerateAssertionArgs>(
  gql`
    query GenerateSamlAssertion($provider: GenerateAssertionProvider) {
      generateAssertion(provider: $provider) {
        response
        acs
        postParams {
          key
          value
        }
      }
    }
  `,
  {key: "generateAssertion", fetchPolicy: "network-only"}
)

interface ProductInfo {
  text: string
  icon: IconTypes
  bgColor: string
  iconColor: string
}

const productInfo: ProductInfo[] = [
  {icon: IconTypes.video, text: "medical.onlinegp.home.about.1", bgColor: "#DBEAFE", iconColor: "#2563EB"},
  {icon: IconTypes.survey, text: "medical.onlinegp.home.about.2", bgColor: "#DCFCE7", iconColor: "#16A34A"},
  {icon: IconTypes.heartOutline, text: "medical.onlinegp.home.about.3", iconColor: "#9333EA", bgColor: "#F3E8FF"}
]

export function OnlineGp() {
  const [isVisible, setIsVisible] = useState(false)
  const theme = useTheme()
  const {firstName, lastName, email} = useAuth().user!

  useEffect(() => {
    trackingOnlineGP.view()
  }, [])

  return (
    <Flex direction="column">
      <Header>
        <Logo />
        <div>
          <H1 marginBottom={2}>
            <FormattedMessage id="medical.onlinegp.headers.onlinegp" />
          </H1>
          <H2>
            <FormattedMessage id="medical.onlinegp.home.heading" />
          </H2>
        </div>
      </Header>
      <Card style={{borderRadius: theme.border.radius.extraLarge}}>
        <Flex gap={2} wrap="wrap">
          <Section>
            <Flex direction="column">
              {productInfo.map((p, i) => (
                <React.Fragment key={i}>
                  <Flex gap={3} alignItems="center">
                    <IconWrapper color={p.bgColor}>
                      <Icon strokeWidth={1} icon={p.icon} size={20} color={p.iconColor} />
                    </IconWrapper>
                    <Markdown source={p.text} />
                  </Flex>
                  {i + 1 < productInfo.length && <Hr />}
                </React.Fragment>
              ))}
            </Flex>
          </Section>
          <Section>
            <Spacing hideOnMobile>
              <LinkToHealthHero />
            </Spacing>
            <Flex justifyContent="space-between" gap={2}>
              <Markdown source="medical.onlinegp.home.yourData.heading" />
              <ExpanderButton
                onClick={() => {
                  if (!isVisible) trackingOnlineGP.expand()
                  setIsVisible((v) => !v)
                }}>
                <Icon icon={isVisible ? IconTypes.chevronUp : IconTypes.chevronDown} size={20} />
              </ExpanderButton>
            </Flex>
            <Spacing marginTop={2} style={{display: isVisible ? "block" : "none"}}>
              <Markdown
                source="medical.onlinegp.home.yourData.text"
                values={{
                  name: `${firstName} ${lastName}`,
                  email,
                  profile: (
                    <a href="/account/profile">
                      <FormattedMessage id="medical.onlinegp.home.yourData.profile" />
                    </a>
                  )
                }}
              />
            </Spacing>
          </Section>
        </Flex>
        <Spacing hideOnDesktop marginTop={2}>
          <LinkToHealthHero full />
        </Spacing>
      </Card>
    </Flex>
  )
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  ${({theme}) => css`
    margin: ${theme.spacing.two} 0;
  `}

  ${({theme}) => theme.mediaQueries.minWidthMedia.phoneLandscape`
    text-align: left;
    margin: ${theme.spacing.six} ${theme.spacing.two};
    flex-direction: row;
    justify-content: flex-start;
    gap: ${theme.spacing.two};
  `}
`
const Logo = styled.img.attrs({src: healthHero, alt: ""})`
  width: 120px;

  ${({theme}) => theme.mediaQueries.minWidthMedia.tablet`
    width: 160px;
  `}

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    width: 180px;
  `}
`

const Hr = styled.hr`
  width: 100%;
  border-top: thin solid ${({theme}) => theme.colors.greys.grey03};
`

const Section = styled.section`
  flex: 1 0 min(370px, 100%);
  display: flex;
  flex-direction: column;
  border: thin solid ${({theme}) => theme.colors.greys.grey03};
  padding: ${({theme}) => theme.spacing.three};
  border-radius: ${({theme}) => theme.border.radius.extraLarge};
`

const IconWrapper = styled.div`
  display: flex;
  padding: 10px;
  background-color: ${({color}) => color};
  border-radius: ${({theme}) => theme.border.radius.extraLarge};
`

const ExpanderButton = styled.div`
  width: 33px;
  height: 33px;
  background-color: ${({theme}) => theme.colors.greys.grey12};
  border-radius: ${({theme}) => theme.border.radius.large};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 6px;
`

function LinkToHealthHero({full}: {full?: boolean}) {
  const [generateSaml, {data: formData, load, loading}] = useGetSamlQuery({provider: "SERVICE_PROVIDER_HEALTHHERO", metadata: undefined})

  useEffect(() => {
    if (formData) navToHealthHero(formData)
  }, [formData])

  return (
    <Flex marginBottom={2} direction="column" gap={1}>
      <div>
        <Button
          full={full}
          disabled={loading}
          onClick={() => {
            trackingOnlineGP.clickCTA()
            generateSaml()
          }}
          primary
          icon={IconTypes.upRightArrow}>
          <FormattedMessage id="medical.onlinegp.home.healthHero.cta" />
        </Button>
      </div>
      <Loading load={load} />
    </Flex>
  )
}

function navToHealthHero(formData: Assertion) {
  if (!(formData.acs && formData.response && formData.postParams)) throw new Error("Something went wrong")

  const form = document.createElement("form")
  form.method = "POST"
  form.action = formData.acs
  form.target = "_blank"

  formData.postParams.concat({key: "SAMLResponse", value: formData.response}).forEach((param) => {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = param.key
    input.value = param.value
    form.appendChild(input)
  })

  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}
